/**
 * A collection path in the record panel: its status, its document count,
 * and the way to the Collections page where the migration is run. The
 * groups that reach a path have no endpoint of their own, so they are not
 * listed here.
 */
import { useQuery } from '@tanstack/react-query';

import { listOrgCollections } from '@optum/api-client/endpoints/collections';
import type { CollectionStatus } from '@optum/api-client/types';

import { formatRecordParam, type RecordRef } from '../../components/shell/record.js';
import {
  RecordActions,
  RecordAsync,
  RecordColumn,
  RecordFacts,
  RecordIdentity,
  RecordMissing,
  RecordOpen,
  RecordSection,
} from '../../components/shell/RecordParts.js';
import { relativeTime } from '../../lib/format.js';
import { migrateHref } from './migrate-href.js';
import { qk } from '../../lib/query-keys.js';
import { Badge, IconTile, Typography, type BadgeTone } from '../../ui/finixt.js';

const STATUS_TONE: Record<CollectionStatus, BadgeTone> = {
  ready: 'ok',
  processing: 'info',
  needs_migration: 'warn',
};

const STATUS_LABEL: Record<CollectionStatus, string> = {
  ready: 'ready',
  processing: 'processing',
  needs_migration: 'needs migration',
};

export function CollectionRecord({ record }: { record: RecordRef }) {
  const orgId = record.orgId ?? '';
  const tree = useQuery({
    queryKey: qk.collections(orgId),
    queryFn: () => listOrgCollections(orgId),
  });

  return (
    <RecordAsync query={tree} record={record}>
      {(t) => {
        const node = t.nodes.find((n) => n.path === record.id);
        if (!node) return <RecordMissing record={record} />;
        const children = t.nodes.filter((n) => n.parent_path === node.path).length;
        return (
          <RecordColumn>
            <RecordIdentity
              avatar={
                <IconTile icon="folder_open" size={40} tint="var(--info-tint)" color="var(--info)" />
              }
              eyebrow={
                <>
                  <Typography variant="eyebrow">Collection</Typography>
                  <Badge tone={STATUS_TONE[node.status]}>{STATUS_LABEL[node.status]}</Badge>
                  {node.dirty ? <Badge tone="warn" mono shape="square">dirty</Badge> : null}
                </>
              }
              name={<span className="mono">{node.path}</span>}
              subtitle={node.description || undefined}
            />
            <RecordSection label="Facts">
              <RecordFacts
                items={[
                  { label: 'Documents', value: String(node.doc_count) },
                  { label: 'Depth', value: String(node.depth) },
                  { label: 'Children', value: String(children) },
                  { label: 'Parent', value: node.parent_path ?? 'root' },
                  { label: 'Updated', value: node.updated_at ? relativeTime(node.updated_at) : '—' },
                ]}
              />
            </RecordSection>
            <RecordActions>
              {node.status === 'needs_migration' ? (
                <RecordOpen to={migrateHref(orgId, node.path)} label="Migrate" />
              ) : (
                <RecordOpen
                  to={`/orgs/${orgId}/collections?record=${encodeURIComponent(formatRecordParam(record))}`}
                  label="Open in Collections"
                />
              )}
            </RecordActions>
          </RecordColumn>
        );
      }}
    </RecordAsync>
  );
}
